import React from "react";
import DOMPurify from "dompurify";
import parse from "html-react-parser";

interface HTMLMessageBodyProps {
	children?: React.ReactNode;
}

function sanitizeBody(body: string): string {
	return DOMPurify.sanitize(body, {
		ALLOWED_TAGS: [
			"a",
			"b",
			"i",
			"em",
			"strong",
			"u",
			"p",
			"br",
			"ul",
			"ol",
			"li",
			"blockquote",
			"code",
			"pre",
			"table",
			"thead",
			"tbody",
			"tr",
			"th",
			"td",
			"label",
		],
		ALLOWED_ATTR: ["href", "target", "rel", "class", "style"],
	});
}

function parseHtmlAndAddLinkAttributes(body: string): HTMLElement {
	const parser = new DOMParser();
	const messageDom = parser
		.parseFromString(body, "text/html")
		.getElementsByTagName("body")[0];
	const links = messageDom.getElementsByTagName("a");
	Array.from(links).forEach((link) => {
		link.setAttribute("target", "_blank");
		link.setAttribute("rel", "noopener noreferrer");
	});

	return messageDom;
}

export default function HTMLMessageBody({ children }: HTMLMessageBodyProps) {
	const isString = typeof children === "string" || children instanceof String;
	if (!isString) {
		return <div />;
	}

	const messageDom = parseHtmlAndAddLinkAttributes(String(children));
	const hasElements = Array.from(messageDom.childNodes).some(
		(node) => node.nodeType === Node.ELEMENT_NODE,
	);

	if (!hasElements) {
		return <div>{messageDom.innerHTML}</div>;
	}

	const message = messageDom.innerHTML;
	const sanitized = sanitizeBody(message);
	return <>{parse(sanitized)}</>;
}
